import React, { Component } from "react";
import { connect } from "react-redux";
import convertToHTML from "draft-convert/lib/convertToHTML";
import selectn from "selectn";
import debounce from "lodash/debounce";

import { loadStory } from "../../../actions/storiesStream";
import { BREAKPOINT_DOWN_SMALL, ENTITY_TYPE, BLOCK_TYPE } from "../../../config/constants";
import { styleToHTML, blockToHTML } from "./conversionHelpers";
import { entityToHTML } from "../../../utils/entityToHTML";
import { handleEmbed } from "../../../utils/embed";
import { isInternalURL } from "../../../utils/utils";
import analytics from "../../../utils/analytics";
import { GET_STORY_ERROR } from "../../../constants/actionTypes";
import Log from "../../../utils/log";

import styles from "./StoryBody.css";
import keepReadingStyles from "../../../components/Library/KeepReading/KeepReading.css";

class StoryBody extends Component {

  constructor(props) {
    super(props);

    this.state = {
      blocks: null,
      keepReadingBlock: null,
      hiddenBlocks: null,
      isOpen: false,
      wordCount: null,
      isFixed: false,
      styledQuoteAttributions: [],
    };
  }

  addInternalClickEventHandler = (identifier) => {
    let elements = document.getElementsByClassName(`${identifier}-${this.props.story.id}`);
    for (let i = 0; i < elements.length; i++) {
      elements[i].onclick = (e) => {
        e.preventDefault();
        analytics.userClicksInternalLink(this.props.stream, elements[i].name);
        this.props.loadStory({ story: identifier }, this.props.story.id)
          .then((res) => {
            if (res.type === GET_STORY_ERROR) {
              window.location = elements[i].href;
            } else {
              setTimeout(() => this.scrollToStory(identifier), 200);
            }
          })
          .catch(() => window.location = elements[i].href);
      };
    }
  };

  scrollToStory = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const bounds = element.getBoundingClientRect();
      window.scrollBy(0, bounds.top - 100);
    }
  };

  componentDidMount() {
    this.scrollListener = debounce(this.handleScroll, 50);
    this.mediaQuery = window.matchMedia(BREAKPOINT_DOWN_SMALL);

    const currentBlocks = selectn("story.blocks.blocks", this.props);
    if (currentBlocks) {
      this.processBlocks(currentBlocks);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.blocks && this.state.blocks) {
      this.state.blocks.filter((block) => block.type === BLOCK_TYPE.EMBED).map(handleEmbed);

      (selectn("story.blocks.entityMap", this.props) || [])
        .filter((entity) => entity.type.toLowerCase() === ENTITY_TYPE.LINK)
        .map((entity) => {
          const matches = isInternalURL(entity.data.url);
          if (matches && matches.length > 1) {
            this.addInternalClickEventHandler(matches[1] || matches[2]);
          }
        });
    }

    if (!prevState.hiddenBlocks && this.state.hiddenBlocks) {
      this.state.hiddenBlocks.filter((block) => block.type === BLOCK_TYPE.EMBED).map(handleEmbed);
    }

    if (this.state.keepReadingBlock && !prevState.keepReadingBlock) {
      this.addKeepReadingClickEventHandler(this.state.keepReadingBlock);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !(this.state.isFixed !== nextState.isFixed || this.state.isOpen !== nextState.isOpen);
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  processBlocks = (blocks) => {
    let newBlocks = [];
    let hiddenBlocks = [];
    let keepReading = null;
    let wordCount = 0;
    let styledQuoteAttributions = [];

    for (let i=0; i < blocks.length; i++) {
      if (!blocks[i]) continue;
      if (i !== 0 && blocks[i].type.toLowerCase() === BLOCK_TYPE.QUOTE_ATTRIBUTION &&
        blocks[i - 1].type.toLowerCase() === BLOCK_TYPE.STYLED_QUOTE) {
        styledQuoteAttributions.push(blocks[i].key);
      }
      if (blocks[i].type.toLowerCase() === BLOCK_TYPE.KEEP_READING) {
        keepReading = blocks[i];
      } else if (keepReading) {
        hiddenBlocks.push(blocks[i]);
        wordCount += blocks[i].text.split(" ").length;
      } else {
        newBlocks.push(blocks[i]);
      }
    }

    if (newBlocks.length === 0) {
      return this.setState({ blocks: null });
    }
    return this.setState({
      blocks: newBlocks,
      keepReadingBlock: keepReading,
      hiddenBlocks,
      wordCount,
      styledQuoteAttributions,
    });
  };

  addListeners = () => {
    if (this.state.keepReadingBlock) {
      this.mediaQuery.addListener(this.handleResize);

      if (this.mediaQuery.matches) {
        window.addEventListener("scroll", this.scrollListener);
      }
    }
  };

  removeListeners = () => {
    this.mediaQuery.removeListener(this.handleResize);
    window.removeEventListener("scroll", this.scrollListener);
  };

  handleScroll = () => {
    if (this.state.keepReadingBlock && this.state.isOpen) {
      const element = document.getElementById(`keep-reading-${this.state.keepReadingBlock.key}`);
      const hidden = document.getElementById(`hidden-${this.props.story.id}`);
      if (element && hidden) {
        const isFixed = this.mediaQuery.matches && this.state.isOpen && !this.isKeepReadingVisible(hidden);
        if (this.state.isFixed !== isFixed) {
          if (isFixed) {
            element.className += (" " + keepReadingStyles.keepReadingFixed);
          } else {
            element.className = element.className.replace(keepReadingStyles.keepReadingFixed, "");
          }
          this.setState({ isFixed });
        }
      }
    }
  };

  handleResize = (event) => {
    if (event.matches) {
      window.addEventListener("scroll", this.scrollListener);
    } else {
      if (this.state.isFixed) {
        this.setState({ isFixed: false });
      }
      window.removeEventListener("scroll", this.scrollListener);
    }
  };

  isKeepReadingVisible = (element) => {
    const bounds = element.getBoundingClientRect();
    return bounds.bottom < (window.innerHeight || document.documentElement.clientHeight);
  };

  addKeepReadingClickEventHandler = (block) => {
    const element = document.getElementById(`keep-reading-${block.key}`);
    if (element) {
      element.onclick = () => {
        const isOpen = !this.state.isOpen;
        this.setState({ isOpen });

        const hidden = document.getElementById(`hidden-${this.props.story.id}`);
        if (hidden) {
          if (isOpen) {
            hidden.style.display = "block";
            hidden.setAttribute("aria-hidden", "false");
            element.setAttribute("aria-expanded", "true");
            element.className += (" " + keepReadingStyles.keepReadingOpen);
          } else {
            hidden.style.display = "none";
            hidden.setAttribute("aria-hidden", "true");
            element.setAttribute("aria-expanded", "false");
            element.className = element.className.replace(keepReadingStyles.keepReadingOpen, "");
          }
        }

        if (isOpen) {
          this.addListeners();
          analytics.userClicksKeepReading(this.props.stream);
        } else {
          this.removeListeners();

          // If next headline is not visible, scroll to just show it
          let bounds = element.getBoundingClientRect();
          let scrollValue = 275 + bounds.top - (window.innerHeight || document.documentElement.clientHeight);

          if (scrollValue > 0) {
            window.scrollBy(0, scrollValue);
          } else if (bounds.top < 60) {
            // If keep reading button is not visible, scroll so it just shows
            window.scrollBy(0, bounds.top - 60);
          }
        }
      };
    }
  };

  getHTML = (blocks, entityMap, extras) => {
    let html = false;
    try{
      html = convertToHTML({
        styleToHTML,
        blockToHTML: blockToHTML(extras),
        // This shouldn't normally work, but we've tricked draft-convert into requiring a version of draft-js where
        // convertToRaw is a noop - thus we can pass raw draft data right into draft-convert
        entityToHTML: entityToHTML(this.props.story, styles),
      })({ blocks, entityMap });
    } catch(e){
      Log.logger(`draft-convert failure. story id: ${this.props.story.id}`, "error", { id: this.props.story.id, error: e });
    }
    return html;
  };

  render() {
    const { hiddenBlocks, keepReadingBlock, wordCount } = this.state;
    if (!this.state.blocks) return null;
    const { entityMap } = this.props.story.blocks;
    const extras = { styledQuoteAttributions: this.state.styledQuoteAttributions };

    const html = this.getHTML(this.state.blocks, entityMap, extras);
    const keepReadingHTML = keepReadingBlock && this.getHTML([keepReadingBlock], entityMap, { wordCount });
    const hiddenHTML = hiddenBlocks && this.getHTML(hiddenBlocks, entityMap, extras);

    // don't render the story if draft-convert failed
    if (!html || (keepReadingHTML && !hiddenHTML)) return null;

    return (
      <div className={styles.root}>
        <div dangerouslySetInnerHTML={{ __html: html }}/>
        {keepReadingHTML && <div dangerouslySetInnerHTML={{ __html: keepReadingHTML }}/>}
        {
          hiddenHTML &&
          <div
            id={`hidden-${this.props.story.id}`}
            style={{ display: "none", paddingBottom: "65px" }}
            aria-hidden={true}
            dangerouslySetInnerHTML={{ __html: hiddenHTML }}/>
        }
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    stream: selectn("navigation.data.section.slug", state),
  };
};

export default connect(mapStateToProps, { loadStory })(StoryBody);



// WEBPACK FOOTER //
// ./src/components/Library/StoryBody/StoryBody.js